import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, formatApiError } from "../context/AuthContext";
import Countdown from "../components/Countdown";
import Seo from "../components/Seo";
import SmartImage from "../components/SmartImage";
import WatchlistButton from "../components/WatchlistButton";
import { ShieldCheck, Hammer, Truck } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const fmt = (n, language) => new Intl.NumberFormat(language === "en" ? "en-IE" : "it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function AuctionDetail() {
  const { language, t } = useLanguage();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [bids, setBids] = useState([]);
  const [amount, setAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [feePct, setFeePct] = useState(10);
  const [activeImg, setActiveImg] = useState("");

  const load = () => {
    axios.get(`${API}/auctions/${id}`).then(r => { setA(r.data); setActiveImg(r.data.image_url); }).catch(() => nav("/browse"));
    axios.get(`${API}/auctions/${id}/bids`).then(r => setBids(r.data));
  };
  useEffect(load, [id]);
  useEffect(() => { axios.get(`${API}/config`).then(r => setFeePct(r.data.platform_fee_percent || 10)).catch(() => {}); }, []);

  const minNext = a ? a.current_bid + Math.max(1, a.current_bid * 0.02) : 0;

  const placeBid = async (e) => {
    e.preventDefault();
    if (!user || user === false) { nav("/login"); return; }
    setPlacing(true);
    try {
      await axios.post(`${API}/auctions/${id}/bid`, { amount: parseFloat(amount) });
      toast.success(t("Offerta piazzata!"));
      setAmount("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setPlacing(false);
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        auction_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
      setPaying(false);
    }
  };

  if (!a) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-stone-500">{t("Caricamento…")}</div>;

  const iAmTopBidder = user && user !== false && bids[0] && bids[0].bidder_name === user.name;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <Seo
        title={a.title}
        description={(a.description || "").slice(0, 155) || `Lotto in asta su Rarium: ${a.title}. Autenticato, spedizione assicurata.`}
        path={`/auction/${a.id}`}
        image={a.image_url}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": a.title,
          "description": a.description,
          "image": a.image_url,
          "category": a.category,
          "brand": { "@type": "Brand", "name": "Rarium" },
          "offers": {
            "@type": "Offer",
            "url": `https://rariumgroup.it/auction/${a.id}`,
            "priceCurrency": "EUR",
            "price": a.current_bid,
            "priceValidUntil": a.ends_at,
            "availability": a.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
            "seller": { "@type": "Organization", "name": a.seller_name || "Rarium" }
          }
        }}
      />
      <div className="text-sm text-stone-500 mb-6">
        <Link to="/browse" className="underline-hover">{t("Aste")}</Link> / <span className="capitalize">{a.category.replace(/-/g," ")}</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div data-testid="auction-main-image" className="aspect-[4/5] bg-stone-100 overflow-hidden">
            <SmartImage src={activeImg || a.image_url} alt={a.title} className="w-full h-full object-cover" fallbackText={a.title} />
          </div>
          {(a.gallery_images && a.gallery_images.length > 0) && (
            <div data-testid="auction-gallery" className="mt-3 grid grid-cols-5 gap-2">
              {[a.image_url, ...a.gallery_images].filter(Boolean).map((url, i) => (
                <button
                  key={i}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(url)}
                  className={`aspect-square overflow-hidden bg-stone-100 border transition-colors ${activeImg === url ? "border-[#8C3A3A]" : "border-stone-200 hover:border-stone-500"}`}
                >
                  <SmartImage src={url} alt="" className="w-full h-full object-cover" fallbackText="" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="overline text-[#8C3A3A]">Lotto #{a.id.slice(-6).toUpperCase()}</div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-serif text-4xl lg:text-5xl leading-tight tracking-tighter">{a.title}</h1>
            <WatchlistButton auctionId={a.id} />
          </div>
          <p className="text-stone-600 leading-relaxed">{a.description}</p>

          <div className="rule pt-6 grid grid-cols-2 gap-6">
            <div>
              <div className="overline text-stone-500">{t("Offerta attuale")}</div>
              <div className="font-serif text-4xl mt-1">{fmt(a.current_bid, language)}</div>
              <div className="text-xs text-stone-500 mt-1">{a.bid_count}{t(" offerte")} · {t("Base")} {fmt(a.starting_bid, language)}</div>
            </div>
            <div>
              <div className="overline text-stone-500">{t("Termina tra")}</div>
              <Countdown endsAt={a.ends_at} className="text-2xl mt-1 block" />
              {a.estimated_low && (
                <div className="text-xs text-stone-500 mt-2">{t("Stima:")} {fmt(a.estimated_low, language)} — {fmt(a.estimated_high, language)}</div>
              )}
            </div>
          </div>

          {/* Fee breakdown — 10% Rarium commission, calcolato lato frontend sul current_bid */}
          <div data-testid="fee-breakdown" className="border border-[#8C3A3A]/30 bg-[#8C3A3A]/5 p-5">
            <div className="flex items-center justify-between">
              <div className="overline text-[#8C3A3A]">{t("Trattenuta Rarium — ")}{feePct}%</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">{t("Calcolata sull'offerta attuale")}</div>
            </div>
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-stone-600">{t("Prezzo di aggiudicazione (offerta attuale)")}</dt>
                <dd data-testid="fee-hammer" className="font-mono">{fmt(a.current_bid, language)}</dd>
              </div>
              <div className="flex justify-between border-t border-[#8C3A3A]/20 pt-1">
                <dt className="text-stone-600">{t("Commissione Rarium")} ({feePct}%)</dt>
                <dd data-testid="fee-commission" className="font-mono text-[#8C3A3A]">− {fmt(a.current_bid * (feePct / 100), language)}</dd>
              </div>
              <div className="flex justify-between border-t border-[#8C3A3A]/20 pt-1 font-semibold">
                <dt>{t("Netto al venditore")}</dt>
                <dd data-testid="fee-net" className="font-mono">{fmt(a.current_bid * (1 - feePct / 100), language)}</dd>
              </div>
            </dl>
            <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
              {t("L'acquirente paga il prezzo di aggiudicazione per intero. Rarium trattiene il ")}{feePct}{t("% come commissione di servizio (autenticazione, marketing, pagamenti). Il venditore riceve il netto tramite bonifico entro 5 giorni lavorativi dalla conferma del pagamento.")}
            </div>
          </div>

          {a.status === "sold" ? (
            <div className="bg-stone-900 text-stone-50 p-6">
              <div className="overline text-[#B49A5F]">{t("Lotto aggiudicato")}</div>
              <div className="font-serif text-2xl mt-2">{t("Questo lotto è stato aggiudicato.")}</div>
            </div>
          ) : iAmTopBidder && a.paid === false && new Date(a.ends_at) < new Date() ? (
            <button
              data-testid="pay-now-btn"
              onClick={pay}
              disabled={paying}
              className="w-full text-xs uppercase tracking-widest font-semibold bg-[#8C3A3A] hover:bg-[#7F3434] text-stone-50 px-8 py-5 transition-colors disabled:opacity-50"
            >{paying ? t("Attendere…") : `${t("Paga ")}${fmt(a.current_bid, language)} →`}</button>
          ) : (
            <form onSubmit={placeBid} className="flex flex-col gap-3">
              <label className="overline text-stone-500">{t("La tua offerta (min. ")}{fmt(minNext, language)})</label>
              <div className="flex gap-3">
                <input
                  data-testid="bid-input"
                  type="number"
                  step="1"
                  min={minNext}
                  placeholder={String(Math.ceil(minNext))}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 border border-stone-300 bg-white px-4 py-4 text-lg font-mono focus:border-stone-900 focus:outline-none"
                  required
                />
                <button
                  data-testid="place-bid-btn"
                  disabled={placing}
                  className="text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-8 py-4 transition-colors disabled:opacity-50"
                >{placing ? "…" : t("Fai offerta")}</button>
              </div>
              {!user || user === false ? (
                <div className="text-xs text-stone-500">{t("Accedi per fare un'offerta. ")}<Link to="/login" className="underline">{t("Accedi")}</Link></div>
              ) : null}
            </form>
          )}

          <div className="rule pt-6 grid grid-cols-3 gap-4 text-xs">
            <TrustBadge icon={<ShieldCheck size={16} />} label={t("Autenticato")} />
            <TrustBadge icon={<Hammer size={16} />} label={t("Curato")} />
            <TrustBadge icon={<Truck size={16} />} label={t("Spedizione assicurata")} />
          </div>

          <div className="rule pt-6">
            <div className="overline text-stone-500 mb-2">{t("Specifiche")}</div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-stone-500">{t("Condizioni")}</dt><dd>{t(a.condition || "—")}</dd>
              <dt className="text-stone-500">{t("Provenienza")}</dt><dd>{a.provenance || "—"}</dd>
              <dt className="text-stone-500">{t("Categoria")}</dt><dd className="capitalize">{a.category.replace(/-/g," ")}</dd>
              <dt className="text-stone-500">{t("Venditore")}</dt><dd>{a.seller_name}</dd>
            </dl>
          </div>

          {/* Spese di spedizione */}
          <div data-testid="shipping-info" className="rule pt-6">
            <div className="overline text-stone-500 mb-2">{t("Spedizione")}</div>
            {a.shipping_cost > 0 ? (
              <div>
                <div className="font-serif text-3xl">{fmt(a.shipping_cost, language)}</div>
                <div className="text-xs text-stone-500 mt-1">{t("Aggiunti al totale al checkout")}</div>
              </div>
            ) : (
              <div>
                <div className="font-serif text-3xl text-emerald-700">{t("Inclusa")}</div>
                <div className="text-xs text-stone-500 mt-1">{t("Nessun costo aggiuntivo per l'acquirente")}</div>
              </div>
            )}
            {a.shipping_notes && (
              <div className="mt-3 text-sm text-stone-600 leading-relaxed">{a.shipping_notes}</div>
            )}
          </div>
        </div>
      </div>

      <section className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="overline text-stone-500 mb-4">{t("Storia delle offerte")}</div>
          <div className="border-t border-stone-200">
            {bids.length === 0 ? (
              <div className="py-8 text-stone-500 text-sm">{t("Ancora nessuna offerta. Sii il primo.")}</div>
            ) : bids.map(b => (
              <div key={b.id} className="flex justify-between items-center py-3 border-b border-stone-200">
                <div className="text-sm">{b.bidder_name}</div>
                <div className="font-mono text-sm">{fmt(b.amount)}</div>
                <div className="text-xs text-stone-500">{new Date(b.created_at).toLocaleString(language === "en" ? "en-IE" : "it-IT")}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 bg-stone-100 p-8">
          <div className="overline text-[#8C3A3A]">{t("Nota del curatore")}</div>
          <p className="mt-4 font-serif text-2xl leading-snug">"Un esemplare che raramente arriva sul mercato in queste condizioni. Consigliato per collezioni tematiche."</p>
          <div className="mt-6 text-sm text-stone-600">— Elena M., Capo Curatore Rarium</div>
        </div>
      </section>
    </div>
  );
}

const TrustBadge = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-2 text-center">
    <div className="text-[#8C3A3A]">{icon}</div>
    <div className="uppercase tracking-widest text-stone-700">{label}</div>
  </div>
);
